import { useState, useCallback } from 'react';
export function useFileUpload({ apiUrl, bearerToken, onError }) {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const uploadFile = useCallback(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await fetch(`${apiUrl}/v1/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${bearerToken}` },
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
            }
            const { id, imageUrl } = await response.json();
            const isImage = file.type.startsWith('image/');
            return {
                id,
                url: imageUrl,
                objectUrl: URL.createObjectURL(file),
                name: file.name,
                size: file.size,
                type: isImage ? 'image' : 'file',
            };
        }
        catch (error) {
            console.error('[useFileUpload] Upload error:', error);
            onError?.(error instanceof Error ? error : new Error('Upload failed'));
            return null;
        }
    }, [apiUrl, bearerToken, onError]);
    const uploadFiles = useCallback(async (files) => {
        setIsUploading(true);
        try {
            const results = await Promise.all(files.map(uploadFile));
            const successful = results.filter((r) => r !== null);
            setUploadedFiles((prev) => [...prev, ...successful]);
            return successful;
        }
        finally {
            setIsUploading(false);
        }
    }, [uploadFile]);
    const removeFile = useCallback((file) => {
        setUploadedFiles((prev) => {
            const toRemove = prev.find((f) => f.name === file.name && f.size === file.size);
            if (toRemove) {
                URL.revokeObjectURL(toRemove.objectUrl);
            }
            return prev.filter((f) => f.name !== file.name || f.size !== file.size);
        });
    }, []);
    const clearFiles = useCallback(() => {
        uploadedFiles.forEach((f) => URL.revokeObjectURL(f.objectUrl));
        setUploadedFiles([]);
    }, [uploadedFiles]);
    const getFileUrls = useCallback(() => {
        const images = uploadedFiles.filter((f) => f.type === 'image').map((f) => f.url);
        const files = uploadedFiles.filter((f) => f.type === 'file').map((f) => f.url);
        return { images, files };
    }, [uploadedFiles]);
    return {
        uploadedFiles,
        isUploading,
        uploadFiles,
        removeFile,
        clearFiles,
        getFileUrls,
    };
}
